import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { User } from 'oidc-client-ts';
import { userManager } from './userManager';
import { redirectTo } from '@utils/navigation';
import { initBrowserDetection } from '@/utils/detectIncognito';
import { UserAccessResponse } from '@/types/userAccess';

interface UserAuthorizationInfo {
  firstName: string;
  lastName: string;
  role: string;
}

interface IdentityAuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAuthorized: boolean | null;
  isLoading: boolean;
  authorizationError: string | null;
  error: string | null;
  userInfo: UserAuthorizationInfo | null;
  userAccessData: UserAccessResponse | null;
  login: (returnPath?: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refetchUserAccess: () => Promise<void>;
}

const IdentityAuthContext = createContext<IdentityAuthContextType | undefined>(
  undefined
);

export const useIdentityAuth = () => {
  const context = useContext(IdentityAuthContext);
  if (!context) {
    throw new Error('useIdentityAuth must be used within IdentityAuthProvider');
  }
  return context;
};

interface IdentityAuthProviderProps {
  children: React.ReactNode;
}

const API_DOMAIN = process.env.REACT_APP_API_DOMAIN || '';
const AUTHORIZATION_ERROR_MESSAGE =
  'You do not have access to this application. Verify your permissions and contact your support team.';
const SERVER_ERROR_MESSAGE =
  'There was a problem verifying your access. Please try again later, or contact your support team if the issue continues.';

export const IdentityAuthProvider: React.FC<IdentityAuthProviderProps> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [authorizationError, setAuthorizationError] = useState<string | null>(
    null
  );
  const [userInfo, setUserInfo] = useState<UserAuthorizationInfo | null>(null);
  const [userAccessData, setUserAccessData] =
    useState<UserAccessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const userRef = React.useRef<User | null>(null);

  const login = useCallback(async (returnPath?: string) => {
    try {
      await userManager.signinRedirect({
        state: { returnTo: returnPath || window.location.pathname },
      });
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage =
        (err as { message?: string } | null)?.message ||
        (err as { error?: string } | null)?.error ||
        String(err);
      setError(errorMessage);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const authority = userManager.settings.authority;
      const postLogoutUri = userManager.settings.post_logout_redirect_uri;
      const storageKey = `oidc.user:${authority}:${userManager.settings.client_id}`;
      const oidcStorage = sessionStorage.getItem(storageKey);
      let idToken: string | undefined;

      if (oidcStorage) {
        try {
          const userData = JSON.parse(oidcStorage);
          idToken = userData.id_token;
        } catch (e) {
          console.error('Auth: Failed to parse OIDC storage', e);
        }
      }

      const endSessionEndpoint = `${authority}/connect/endsession`;
      const logoutUrl = `${endSessionEndpoint}?post_logout_redirect_uri=${encodeURIComponent(postLogoutUri ?? '')}${idToken ? `&id_token_hint=${encodeURIComponent(idToken)}` : ''}`;

      sessionStorage.removeItem(storageKey);

      redirectTo(logoutUrl);
    } catch (err) {
      console.error('Logout error:', err);
      const errorMessage =
        (err as { message?: string } | null)?.message ||
        (err as { error?: string } | null)?.error ||
        String(err);
      setError(errorMessage);
    }
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const currentUser = await userManager.getUser();

      if (!currentUser || currentUser.expired) {
        try {
          const renewedUser = await userManager.signinSilent();
          if (!renewedUser) return null;
          setUser(renewedUser);
          return renewedUser.access_token;
        } catch (silentError) {
          console.warn('Silent renew failed:', silentError);
          return null;
        }
      }

      return currentUser.access_token;
    } catch (err) {
      console.error('Get access token error:', err);
      return null;
    }
  }, []);

  const validateUserAuthorization = useCallback(
    async (accessToken: string): Promise<boolean> => {
      try {
        const response = await fetch(`${API_DOMAIN}user-access`, {
          method: 'GET',
          cache: 'no-store',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data: UserAccessResponse = await response.json();
          if (!data.hasSmpAccess) {
            setIsAuthorized(false);
            setAuthorizationError(AUTHORIZATION_ERROR_MESSAGE);
            setUserInfo(null);
            setUserAccessData(null);
            return false;
          }
          const fullName = data.fullName || '';
          const nameParts = fullName.split(' ', 2);
          const isAdmin = (data.organisations || []).some(
            org => org.hasSmpAccess
          );
          setUserInfo({
            firstName: nameParts[0] || '',
            lastName: nameParts.length > 1 ? nameParts[1] : '',
            role: isAdmin ? 'Administrator' : 'User',
          });
          setUserAccessData(data);
          setIsAuthorized(true);
          setAuthorizationError(null);
          return true;
        } else {
          console.warn(
            'Auth: User authorization failed with status:',
            response.status
          );
          setIsAuthorized(false);
          if (response.status >= 500) {
            setAuthorizationError(SERVER_ERROR_MESSAGE);
          } else {
            setAuthorizationError(AUTHORIZATION_ERROR_MESSAGE);
          }
          setUserInfo(null);
          setUserAccessData(null);
          return false;
        }
      } catch (err) {
        console.error('Auth: Authorization validation error:', err);
        setIsAuthorized(false);
        setAuthorizationError(SERVER_ERROR_MESSAGE);
        setUserInfo(null);
        setUserAccessData(null);
        return false;
      }
    },
    []
  );

  const refetchUserAccess = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await fetch(`${API_DOMAIN}user-access`, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data: UserAccessResponse = await response.json();
        setUserAccessData(data);
      }
    } catch (err) {
      console.error('Auth: Failed to refetch user access:', err);
    }
  }, [getAccessToken]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        setAuthorizationError(null);
        const currentUser = await userManager.getUser();

        if (currentUser && !currentUser.expired) {
          const isFirstLoad = userRef.current === null;
          userRef.current = currentUser;
          if (isFirstLoad) {
            await validateUserAuthorization(currentUser.access_token);
          }
          setUser(currentUser);
        } else {
          setUser(null);
          setIsAuthorized(false);
          setUserInfo(null);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        const errorMessage =
          (err as { message?: string } | null)?.message ||
          (err as { error?: string } | null)?.error ||
          String(err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (!userRef.current) {
      initAuth();
    }

    const handleUserLoaded = async (loadedUser: User) => {
      if (userRef.current?.id_token === loadedUser.id_token) {
        setIsLoading(false);
        return;
      }
      const isFirstLoad = userRef.current === null;
      userRef.current = loadedUser;
      setUser(loadedUser);
      setError(null);

      if (!isFirstLoad) {
        return;
      }

      setIsLoading(true);
      await validateUserAuthorization(loadedUser.access_token);
      setIsLoading(false);
    };
    const handleUserUnloaded = () => {
      setUser(null);
      setIsAuthorized(null);
      setUserInfo(null);
    };

    const handleUserSignedOut = async () => {
      const browserDetection = await initBrowserDetection();
      if (!browserDetection.isPrivate) {
        userRef.current = null;
        await userManager.removeUser().catch(() => undefined);
        setUser(null);
        setIsAuthorized(false);
        setUserInfo(null);
      }
    };

    const handleAccessTokenExpired = () => {
      /* Token expired, will be renewed automatically via silent renew */
    };

    const handleSilentRenewError = (err: Error) => {
      console.error('IdentityAuth: Silent renew error:', err);
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addUserSignedOut(handleUserSignedOut);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);
    userManager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [validateUserAuthorization]);

  useEffect(() => {
    const recoverSessionOnFocus = async () => {
      if (document.visibilityState !== 'visible') return;
      // Only relevant if there was an authenticated session that may have lapsed
      // while the tab was idle/backgrounded.
      if (!userRef.current) return;
      try {
        const currentUser = await userManager.getUser();
        if (currentUser && !currentUser.expired) return;
        // The access token lapsed while idle. Try a silent renew first; on Safari
        // the renew iframe is usually blocked by ITP, so fall back to a full-page
        // interactive redirect, which is not subject to ITP.
        try {
          const renewedUser = await userManager.signinSilent();
          if (renewedUser) {
            userRef.current = renewedUser;
            setUser(renewedUser);
            return;
          }
        } catch (silentError) {
          console.warn('Session recovery: silent renew failed, redirecting to login', silentError);
        }
        await login();
      } catch (err) {
        console.error('Session recovery error:', err);
      }
    };

    document.addEventListener('visibilitychange', recoverSessionOnFocus);
    return () => {
      document.removeEventListener('visibilitychange', recoverSessionOnFocus);
    };
  }, [login]);

  const value: IdentityAuthContextType = {
    user,
    isAuthenticated: !!user && !user.expired,
    isAuthorized,
    isLoading,
    authorizationError,
    error,
    userInfo,
    userAccessData,
    login,
    logout,
    getAccessToken,
    refetchUserAccess,
  };

  return (
    <IdentityAuthContext.Provider value={value}>
      {children}
    </IdentityAuthContext.Provider>
  );
};
